import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { of } from 'rxjs';
import { MessageService } from '../service-ui/message.service';
import { HttpClient } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';
import { StartupService } from '../service-ui/startup.service';
import {environment} from "../../environments/environment";
import {DataService} from "./data-service";
import {Facility} from "../../model/Facility";
import {ShippingOption} from "../../model/cart/ShippingOption";
import {Address} from "../../model/Address";
import {ShippingItem} from "../../model/cart/ShippingItem";
import {ShippingPackage} from "../../model/cart/ShippingPackage";

@Injectable()
export class PackageAndShippingComputationService extends DataService {

  constructor(private http: HttpClient, private messageService: MessageService, private startupService: StartupService) {
    super(startupService);
    startupService.setPackageAndShippingComputationService(this);
  }

  /** PUT: sendScan */
  scan(value: string ): Observable<String> {
    const url = `${environment.apiBaseURL}scan/${value}`;
    return this.http.post(url, undefined, this.startupService.getHttpOptions()).pipe(
      tap(_ => this.log(`scan value:${value}`)),
      catchError(this.handleError<any>(`Error scan value:${value}`))
    );
  }
  getPackages(carrier: string, serviceLevel: string, facilityId: number, shippingItems: ShippingItem[], packItemsInSeparateBoxes: boolean, useCaseQuantities: boolean, computationVersion: number): Observable<any> {
      const url = `${environment.apiBaseURL}packageandshippingcomputation/packages`;
      return this.http.post(url, { carrier: carrier, serviceLevel: serviceLevel, facility: facilityId, shippingItems: shippingItems, packItemsInSeparateBoxes: packItemsInSeparateBoxes, useCaseQuantities: useCaseQuantities, computationVersion: computationVersion}, this.startupService.getHttpOptions()).pipe(
        tap(_ => this.log(`getPackages value:${{ carrier: carrier, serviceLevel: serviceLevel, facility: facilityId, shippingItems: shippingItems, packItemsInSeparateBoxes: packItemsInSeparateBoxes, useCaseQuantities: useCaseQuantities, computationVersion: computationVersion}}`)),
        catchError(this.handleError<any>(`Error getPackages value:${{ carrier: carrier, serviceLevel: serviceLevel, facility: facilityId, shippingItems: shippingItems, packItemsInSeparateBoxes: packItemsInSeparateBoxes, useCaseQuantities: useCaseQuantities, computationVersion: computationVersion}}`))
      );
  }

  /**
   * Build an explicit request DTO for `POST /packageandshippingcomputation/shippingoptions`
   * rather than serialising the whole `ShippingPackage` instance. The
   * class carries several fields that are internal client state
   * (`status`, `computationStatus`, `shippingOptions[]`) and MUST NOT
   * leak onto the wire — sending them once caused the server to log
   * shape-mismatch warnings and (more importantly) meant a missing
   * `packageTypeId` was silently omitted with no diagnostic on the
   * request side.
   *
   * The wire contract expected by the server (informal — no shared
   * contract file for this legacy endpoint; see
   * `docs/contracts/outbox/_sent/2026-07-08-packageandshippingcomputation-packagetypeid-field.md`)
   * is a package description shaped like the elements of the
   * `packages[]` array returned by the companion `getPackages`
   * endpoint: `{ packageTypeId, packageIndex?, width, height, depth,
   * weight, quantity, itemsPerPackage?, packingCost? }`.
   *
   * If `packageTypeId` is missing on the source `ShippingPackage`, we
   * attempt a client-side dimension-based lookup via
   * `resolvePackageTypeIdByDimensions()` (the client has the full
   * `PackageType` catalog loaded at startup). This makes the rating
   * call correct even when the server-side packer response does not
   * echo the id back — the historical failure mode before the
   * 2026-07-09 fix. If the id still cannot be resolved, we log a
   * `console.error` diagnostic (rather than silently sending a payload
   * the rating service can only key on dimensions).
   */
  getShippingOptions(carrier: string, serviceLevel: string, facilityId: number, toAddress: Address, shippingPackage: ShippingPackage, computationVersion: number): Observable<any> {
    const url = `${environment.apiBaseURL}packageandshippingcomputation/shippingoptions`;
    let packageTypeId = shippingPackage.packageTypeId;
    if (packageTypeId === undefined || packageTypeId === null) {
      const resolved = this.resolvePackageTypeIdByDimensions(
        carrier,
        shippingPackage.height,
        shippingPackage.width,
        shippingPackage.depth
      );
      if (resolved !== undefined) {
        packageTypeId = resolved;
        // Persist back onto the source ShippingPackage so downstream
        // consumers (order-placement, per-package cost tables) see the
        // resolved id too — otherwise every reader would repeat the
        // lookup and any that don't run it would fall back to the old
        // "packageTypeId is undefined" behaviour.
        shippingPackage.packageTypeId = resolved;
      } else {
        console.error(
          'PackageAndShippingComputationService.getShippingOptions: could not resolve packageTypeId — server response lacked it AND no local PackageType matches the packer dimensions',
          {
            carrier,
            serviceLevel,
            facilityId,
            dims: {
              height: shippingPackage.height,
              width: shippingPackage.width,
              depth: shippingPackage.depth,
              weight: shippingPackage.weight
            }
          }
        );
      }
    }
    const wirePackage: any = {
      packageTypeId: packageTypeId,
      width: shippingPackage.width,
      height: shippingPackage.height,
      depth: shippingPackage.depth,
      weight: shippingPackage.weight,
      quantity: shippingPackage.quantity
    };
    if (shippingPackage.packageIndex !== undefined && shippingPackage.packageIndex !== null) {
      wirePackage.packageIndex = shippingPackage.packageIndex;
    }
    if (shippingPackage.itemsPerPackage !== undefined && shippingPackage.itemsPerPackage !== null) {
      wirePackage.itemsPerPackage = shippingPackage.itemsPerPackage;
    }
    if (shippingPackage.packingCost !== undefined && shippingPackage.packingCost !== null) {
      wirePackage.packingCost = shippingPackage.packingCost;
    }
    const body = {
      carrier: carrier,
      serviceLevel: serviceLevel,
      toAddress: toAddress,
      facilityId: facilityId,
      shippingPackage: wirePackage,
      computationVersion: computationVersion
    };
    return this.http.post(url, body, this.startupService.getHttpOptions()).pipe(
      tap(_ => this.log(`getShippingOptions carrier=${carrier} serviceLevel=${serviceLevel} facility=${facilityId} packageTypeId=${packageTypeId} computationVersion=${computationVersion}`)),
      catchError(this.handleError<any>(`Error getShippingOptions carrier=${carrier} serviceLevel=${serviceLevel} facility=${facilityId} packageTypeId=${packageTypeId} computationVersion=${computationVersion}`))
    );
  }

  /**
   * Client-side fallback lookup for the server-owned `packageTypeId`.
   * Matches (carrier, height, width, depth) against the
   * `PackageTypeService` catalogue that is loaded at startup.
   *
   * Used when the packer response from
   * `POST /packageandshippingcomputation/packages` omits the id — see
   * the outbox request
   * `docs/contracts/outbox/_sent/2026-07-08-packageandshippingcomputation-packagetypeid-field.md`.
   * Once the server reliably emits `packageTypeId` (or `packageType_ID`)
   * this fallback becomes a no-op safety net.
   *
   * Matching strategy:
   *   1. Prefer an exact (carrier, height, width, depth) match.
   *   2. If none, permit a carrier-agnostic exact-dimension match
   *      (the packer sometimes returns a "carrier-neutral" box when
   *      the shipping-picker is showing rates for multiple carriers).
   *   3. If none, permit an orientation-insensitive match — treat
   *      `(H, W)` and `(W, H)` as equivalent (the packer may transpose
   *      the two long dimensions relative to the catalogue row).
   *   4. If none, return `undefined` and let the caller diagnose.
   *
   * Dimensions are compared with a small epsilon (0.05 in) because the
   * packer occasionally returns computed floats
   * (e.g. `0.19225919999999996`) that differ from the catalogue's
   * clean whole-number values only by floating-point rounding.
   */
  resolvePackageTypeIdByDimensions(carrier: string, height: number, width: number, depth: number): number | undefined {
    if (height === undefined || width === undefined || depth === undefined) {
      return undefined;
    }
    const notificationsService = this.startupService.getNotificationsService && this.startupService.getNotificationsService();
    if (!notificationsService || !notificationsService.services) { return undefined; }
    const packageTypeService: any = notificationsService.services['packageTypeService'];
    if (!packageTypeService || !packageTypeService.objects) { return undefined; }
    const candidates: any[] = packageTypeService.objects;
    const eps = 0.05;
    const dimsMatch = (a: number, b: number) => Math.abs((a || 0) - (b || 0)) <= eps;
    // 1. carrier + all three dims
    let hit = candidates.find(pt =>
      pt && pt.carrier === carrier &&
      dimsMatch(pt.height, height) && dimsMatch(pt.width, width) && dimsMatch(pt.depth, depth)
    );
    // 2. any carrier, exact dims
    if (!hit) {
      hit = candidates.find(pt =>
        pt && dimsMatch(pt.height, height) && dimsMatch(pt.width, width) && dimsMatch(pt.depth, depth)
      );
    }
    // 3. carrier + orientation-insensitive dims (swap H/W)
    if (!hit) {
      hit = candidates.find(pt =>
        pt && pt.carrier === carrier &&
        dimsMatch(pt.height, width) && dimsMatch(pt.width, height) && dimsMatch(pt.depth, depth)
      );
    }
    // 4. any carrier, orientation-insensitive dims
    if (!hit) {
      hit = candidates.find(pt =>
        pt && dimsMatch(pt.height, width) && dimsMatch(pt.width, height) && dimsMatch(pt.depth, depth)
      );
    }
    return hit ? hit.ID : undefined;
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T> (operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // TODO: send the error to remote logging infrastructure
      console.error(error, operation); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
  private log(message: string) {
    this.messageService.add('UserService: ' + message);
  }

  reset (filter: string = '') {
  }
}
